<script setup lang="ts">
  import {
    computed,
    getCurrentInstance,
    onScopeDispose,
    ref,
    watch,
  } from 'vue';
  import { useNow } from '@vueuse/core';
  import { ReservationsService } from '@/api/services/reservations.service';
  import PosUiButton from '@/components/pos-v2/ui/PosUiButton.vue';
  import PosUiField from '@/components/pos-v2/ui/PosUiField.vue';
  import PosUiInput from '@/components/pos-v2/ui/PosUiInput.vue';
  import V2ReservationPartyQuickRow from '@/components/v2/reservations/V2ReservationPartyQuickRow.vue';
  import V2ReservationTablePicker from '@/components/v2/reservations/V2ReservationTablePicker.vue';
  import type { IReservationTablePickerValidity } from '@/interfaces/IReservationUi';
  import { useAuthStore } from '@/composition/use-auth';
  import { useModal } from '@/composition/use-modal';
  import { useUi } from '@/composition/use-ui';
  import {
    formatReservationPhoneInput,
    normalizeReservationPhone,
  } from '@/domain/reservations/reservation-phone';
  import type { ITableReservationAvailability } from '@/domain/reservations/table-availability';
  import { PermissionType } from '@/enums/PermissionType';
  import { AlertType } from '@/interfaces/IAlert';
  import { useReservationsStore } from '@/stores/reservations.store';
  import { useRestaurantStore } from '@/stores/restaurant.store';
  const props = defineProps<{ partySize?: number; tableIds?: number[] }>();
  const proxy = getCurrentInstance()!.proxy;
  const modals = useModal();
  const ui = useUi();
  const auth = useAuthStore();
  const restaurant = useRestaurantStore();
  const reservations = useReservationsStore();
  const now = useNow({ interval: 30000 });
  const modalName = 'v2-reservation-walk-in';
  const restaurantId = restaurant.restaurant?.id ?? 0;
  const branchId = restaurant.selectedBranch?.id ?? 0;
  const stringId = restaurant.restaurant?.stringId ?? '';
  const country = restaurant.restaurant?.country ?? 'AR';
  const party = ref(props.partySize ?? 2);
  const choosingTable = ref(!!props.partySize);
  const selected = ref([...(props.tableIds ?? [])]);
  const more = ref(false);
  const name = ref('');
  const phone = ref('');
  const nameError = ref('');
  const phoneError = ref('');
  const error = ref('');
  const saving = ref(false);
  const pickerRevision = ref(0);
  const availability = ref<ITableReservationAvailability[]>([]);
  const validity = ref<IReservationTablePickerValidity>({
    loading: true,
    valid: false,
    force: false,
  });
  let alive = true;
  onScopeDispose(() => {
    alive = false;
  });
  const current = () =>
    alive &&
    restaurant.restaurant?.id === restaurantId &&
    restaurant.selectedBranch?.id === branchId;
  watch(
    () => [restaurant.restaurant?.id, restaurant.selectedBranch?.id],
    () => {
      if (!current()) modals.hide(modalName);
    },
    { flush: 'sync' }
  );
  const canManage = computed(() =>
    auth.permissionSet.has(PermissionType.ReservationsManage)
  );
  const tr = (key: string, params?: Record<string, unknown>) =>
    String(proxy.$t(key, params));
  function chooseParty() {
    choosingTable.value = true;
  }
  function setPhone(value: string) {
    phone.value = formatReservationPhoneInput(value, country);
  }
  function openTable(tableId: number) {
    if (auth.permissionSet.has(PermissionType.OrdersManage))
      void proxy.$router.push({
        name: 'restaurantPosTable',
        params: { restaurantId: stringId, tableId: String(tableId) },
      });
  }
  async function seat(ids: number[]) {
    if (
      !current() ||
      !canManage.value ||
      saving.value ||
      validity.value.loading ||
      ids.length !== 1
    )
      return;
    const table = availability.value.find(
      (choice) => choice.table.id === ids[0]
    );
    if (!table?.selectable || party.value < 1 || party.value > 50) return;
    nameError.value =
      name.value.trim() && name.value.trim().length < 2
        ? tr('reservations.form.nameRequired')
        : '';
    const phoneE164 = phone.value.trim()
      ? normalizeReservationPhone(phone.value, country)
      : null;
    phoneError.value =
      phone.value.trim() && !phoneE164
        ? tr('reservations.form.phoneInvalid')
        : '';
    if (nameError.value || phoneError.value) {
      more.value = true;
      return;
    }
    saving.value = true;
    error.value = '';
    try {
      const result = await ReservationsService.createWalkIn({
        restaurantId,
        branchId,
        partySize: party.value,
        tableIds: ids,
        guestName: name.value.trim() || undefined,
        guestPhone: phoneE164,
      });
      if (!current()) return;
      reservations.patchReservation(result.reservation);
      ui.showToast(
        AlertType.Success,
        tr('reservations.walkIn.seated', { table: table.table.name }),
        auth.permissionSet.has(PermissionType.OrdersManage)
          ? {
              text: tr('reservations.detail.openTable'),
              cb: () => openTable(result.tableId),
            }
          : undefined
      );
      proxy.$parent?.$emit('saved', result.reservation);
      modals.hide(modalName);
    } catch (cause) {
      if (!current()) return;
      saving.value = false;
      error.value = tr('reservations.walkIn.error');
      selected.value = [];
      pickerRevision.value++;
      ui.serverError(cause, error.value);
    }
  }
  function waitlist() {
    if (saving.value || !canManage.value) return;
    proxy.$parent?.$emit('waitlist', party.value);
    modals.hide(modalName);
  }
</script>
<template>
  <div class="reservation-walk-in-modal">
    <header class="modal-header">
      <h2>{{ $t('reservations.walkIn.title') }}</h2>
      <p>
        {{
          $t(
            choosingTable
              ? 'reservations.walkIn.pickTable'
              : 'reservations.walkIn.subtitle'
          )
        }}
      </p>
    </header>
    <div class="modal-content">
      <V2ReservationPartyQuickRow
        v-model="party"
        :disabled="saving || !canManage"
        @choose="chooseParty"
      />
      <section v-show="choosingTable" class="table-step">
        <PosUiButton
          class="details-toggle"
          color="neutral"
          variant="ghost"
          :icon="more ? 'chevron-up' : 'chevron-down'"
          :aria-expanded="more"
          :disabled="saving"
          @click="more = !more"
          >{{
            $t(
              more
                ? 'reservations.form.lessDetails'
                : 'reservations.form.moreDetails'
            )
          }}</PosUiButton
        >
        <div v-show="more" class="guest-fields">
          <PosUiField
            :label="$t('reservations.walkIn.name')"
            input-id="reservation-walk-in-name"
            :error="nameError"
            ><PosUiInput
              id="reservation-walk-in-name"
              v-model="name"
              :maxlength="128"
              :disabled="saving" /></PosUiField
          ><PosUiField
            :label="$t('reservations.walkIn.phone')"
            input-id="reservation-walk-in-phone"
            :error="phoneError"
            ><PosUiInput
              id="reservation-walk-in-phone"
              :value="phone"
              inputmode="tel"
              icon="phone"
              :maxlength="32"
              :disabled="saving"
              @input="setPhone"
          /></PosUiField>
        </div>
        <V2ReservationTablePicker
          :key="pickerRevision"
          v-model="selected"
          :party-size="party"
          :starts-at="now.toISOString()"
          mode="now"
          :multiple="false"
          :disabled="saving || !canManage"
          @availability="availability = $event"
          @validity="validity = $event"
          @select="seat"
        />
        <p v-if="error" class="error" role="alert">{{ error }}</p>
      </section>
    </div>
    <footer class="modal-actions">
      <PosUiButton
        color="neutral"
        variant="ghost"
        :disabled="saving || !canManage"
        @click="waitlist"
        >{{ $t('reservations.walkIn.toWaitlist') }}</PosUiButton
      ><PosUiButton
        color="neutral"
        variant="outline"
        :disabled="saving"
        @click="modals.hide(modalName)"
        >{{ $t('cancel') }}</PosUiButton
      >
    </footer>
  </div>
</template>
<style scoped lang="scss">
  .reservation-walk-in-modal {
    @apply tw-flex tw-min-h-0 tw-flex-col tw-bg-white;
    max-height: 90dvh;
    .modal-header {
      @apply tw-border-b tw-border-neutral-200 tw-px-5 tw-py-4;
      h2 {
        @apply tw-m-0 tw-text-lg tw-font-semibold tw-text-neutral-900;
      }
      p {
        @apply tw-m-0 tw-mt-1 tw-text-sm tw-text-neutral-500;
      }
    }
    .modal-content {
      @apply tw-flex tw-min-h-0 tw-flex-col tw-gap-5 tw-overflow-y-auto tw-p-5;
    }
    .table-step {
      @apply tw-flex tw-flex-col tw-gap-4;
    }
    .guest-fields {
      @apply tw-grid tw-gap-3 sm:tw-grid-cols-2;
    }
    .details-toggle {
      @apply tw-self-start;
    }
    .modal-actions {
      @apply tw-flex tw-flex-none tw-flex-wrap tw-items-center tw-justify-between tw-gap-2 tw-border-t tw-border-neutral-200 tw-p-4;
    }
    .error {
      @apply tw-m-0 tw-text-sm tw-text-red-600;
    }
  }
</style>
